import type { StackTowerConfig } from './rules.ts';
import { sweepBounds, sweepPosition } from './movement.ts';
import { footprintWidth, resolvePlacement, type BlockFootprint, type PlacementOutcome } from './placement.ts';

export interface TowerRunState {
  /** Opaque, caller-supplied (e.g. `crypto.randomUUID()`) — a random-UUID generator isn't pure, so it never gets minted inside this package. */
  readonly runId: string;
  readonly phase: 'playing' | 'game_over';
  readonly score: number;
  /** Blocks successfully placed since the last reset (fresh start, or a timed-mode miss) — NOT the same as `placementCount`, which never resets. Drives both the score multiplier and how tall the tower visually reads; see `multiplierForClimb`. */
  readonly climbBlocks: number;
  readonly topFootprint: BlockFootprint;
  readonly movingBlockWidth: number;
  readonly spawnedAtMs: number;
  readonly sweepStartEdge: 'low' | 'high';
  readonly perfectStreak: number;
  readonly maxPerfectStreak: number;
  readonly perfectCount: number;
  readonly placementCount: number;
  readonly smallestOverlapSize: number | null;
  readonly offsetSum: number;
  readonly finalOutcome: Extract<PlacementOutcome, { kind: 'miss' }> | null;
}

export function createInitialTowerState(config: StackTowerConfig, runId: string, nowMs: number): TowerRunState {
  const half = config.startingBlockWidth / 2;
  const centre = config.trackWidth / 2;
  const topFootprint: BlockFootprint = { min: centre - half, max: centre + half };
  return {
    runId,
    phase: 'playing',
    score: 0,
    climbBlocks: 0,
    topFootprint,
    movingBlockWidth: config.startingBlockWidth,
    spawnedAtMs: nowMs,
    sweepStartEdge: 'low',
    perfectStreak: 0,
    maxPerfectStreak: 0,
    perfectCount: 0,
    placementCount: 0,
    smallestOverlapSize: null,
    offsetSum: 0,
    finalOutcome: null,
  };
}

/**
 * The one function both `currentMovingBlockFootprint` (the per-frame
 * "peek", called every rAF tick to draw) and `placeBlock` (the actual
 * hit-test) route through. This is the single most safety-critical property
 * in the whole package (spec §87): if the renderer's draw and the reducer's
 * judgment ever computed position two slightly different ways, a player
 * could see the block in one place and get scored against another.
 */
function computeMovingFootprint(state: TowerRunState, nowMs: number, config: StackTowerConfig): BlockFootprint {
  const { low, high } = sweepBounds(config.trackWidth, state.movingBlockWidth);
  const elapsedMs = nowMs - state.spawnedAtMs;
  const centre = sweepPosition(elapsedMs, low, high, config.moveSpeedUnitsPerSec, state.sweepStartEdge);
  const half = state.movingBlockWidth / 2;
  return { min: centre - half, max: centre + half };
}

/** Safe to call every rAF frame — a pure "peek", never mutates anything. Null once the run is over. */
export function currentMovingBlockFootprint(state: TowerRunState, nowMs: number, config: StackTowerConfig): BlockFootprint | null {
  if (state.phase !== 'playing') return null;
  return computeMovingFootprint(state, nowMs, config);
}

/**
 * The score multiplier for the *next* block, given how many blocks are
 * already stacked in the current unbroken climb. ×1 for blocks 1 through
 * `scoreMultiplierBlocksPerTier`, ×2 for the next tier, and so on — the one
 * formula both `placeBlock` (to score) and the HUD (to display it live)
 * share, so they can never disagree.
 */
export function multiplierForClimb(climbBlocks: number, config: Pick<StackTowerConfig, 'scoreMultiplierBlocksPerTier'>): number {
  return 1 + Math.floor(climbBlocks / config.scoreMultiplierBlocksPerTier);
}

export type TowerPlaceResult =
  | { readonly ok: true; readonly state: TowerRunState; readonly outcome: PlacementOutcome }
  | { readonly ok: false; readonly reason: string; readonly state: TowerRunState };

/**
 * Resolves one placement input. No `RESET` event exists here (unlike
 * game-core's round.ts) — a restart is a fresh `createInitialTowerState()`
 * call, not an in-place reset, since there's no multi-round match wrapper
 * to return to an idle phase within.
 */
export function placeBlock(state: TowerRunState, nowMs: number, config: StackTowerConfig): TowerPlaceResult {
  if (state.phase !== 'playing') {
    return { ok: false, reason: 'the run is already over', state };
  }

  const moving = computeMovingFootprint(state, nowMs, config);
  const outcome = resolvePlacement(state.topFootprint, moving, config);

  if (outcome.kind === 'miss') {
    return { ok: true, outcome, state: { ...state, phase: 'game_over', finalOutcome: outcome } };
  }

  const newWidth = footprintWidth(outcome.newFootprint);
  const perfectStreak = outcome.perfect ? state.perfectStreak + 1 : 0;
  const nextState: TowerRunState = {
    ...state,
    score: state.score + multiplierForClimb(state.climbBlocks, config),
    climbBlocks: state.climbBlocks + 1,
    topFootprint: outcome.newFootprint,
    movingBlockWidth: newWidth,
    spawnedAtMs: nowMs,
    sweepStartEdge: state.sweepStartEdge === 'low' ? 'high' : 'low',
    perfectStreak,
    maxPerfectStreak: Math.max(state.maxPerfectStreak, perfectStreak),
    perfectCount: state.perfectCount + (outcome.perfect ? 1 : 0),
    placementCount: state.placementCount + 1,
    smallestOverlapSize: state.smallestOverlapSize === null ? newWidth : Math.min(state.smallestOverlapSize, newWidth),
    offsetSum: state.offsetSum + Math.abs(outcome.offsetFromPreviousCentre),
  };
  return { ok: true, outcome, state: nextState };
}
